import { replacePlaceholders } from './helper';
import ERROR_MESSAGE_COPIES from '@/constants/error-messages/index';

/**
 * Returns the mandatory error message for a given field scheme.
 * @param fieldScheme The field's schema object
 */
export function getMandatoryErrorMessage(fieldScheme, isSubmittalsEnabled = false) {
    if (!fieldScheme) return 'This field is required';
    // Get the primary type (if type is an array, use the first non-null type)
    let type = fieldScheme.type;
    if (Array.isArray(type)) {
        type = type.find(t => t !== 'null') || 'string';
    }

    // Handle special cases based on field properties
    if (fieldScheme.enum) {
        type = 'enum';
    } else if (type === 'string') {
        if (fieldScheme.format?.includes('date')) {
            type = 'date';
        }
    } else if (type === 'integer' || (type === 'number' && fieldScheme.format === 'double')) {
        type = 'number';
    } else if (type === 'array' && isSubmittalsEnabled) {
        type = fieldScheme['x-uitype'] === 'multi-select' ? 'array' : 'arrayAdd';
    }

    // Get the appropriate message template based on type.
    // When flag is off, skip the submittal-specific array/arrayAdd keys so BAU array fields
    // fall back to the generic string message — same behaviour as before these keys existed.
    const resolvedType = (!isSubmittalsEnabled && (type === 'array' || type === 'arrayAdd')) ? 'string' : type;
    return ERROR_MESSAGE_COPIES.mandatory[resolvedType] ?? ERROR_MESSAGE_COPIES.mandatory.string;
}

/**
 * Extracts conditional properties from the condition object
 * @param condition The condition object from schema
 * @returns Object containing field names and their values
 */
function extractConditionalProperties(condition) {
    const isElseCondition = condition.if?.not?.properties != null;
    const conditionalProperties = condition.if.properties ?? condition.if?.not?.properties;
    
    if (!conditionalProperties) {
        return null;
    }

    const result = {};
    for (const fieldName in conditionalProperties) {
        const property = conditionalProperties[fieldName];
        if (property.const !== undefined) {
            result[fieldName] = property.const;
        } else if (property.enum !== undefined) {
            result[fieldName] = property.enum;
        } else {
            result[fieldName] = property;
        }
    }

    return { result, isElseCondition };
}

/**
 * Gets the field label from schema properties
 * @param schema The validation schema
 * @param fieldName The field name
 * @returns The field label or field name
 */
function getFieldLabel(schema, fieldName) {
    return schema?.properties?.[fieldName]?.title ?? fieldName;
}

/**
 * Formats object values for error messages
 * @param value The value to format
 * @returns Formatted string value
 */
function formatObjectValue(value) {
    if (value.exclusiveMinimum !== undefined) {
        return `greater than ${value.exclusiveMinimum}`;
    }
    if (value.minimum !== undefined) {
        return `at least ${value.minimum}`;
    }
    if (value.maximum !== undefined) {
        return `at most ${value.maximum}`;
    }
    if (value.const !== undefined) {
        return `${value.const}`;
    }
    if (value.enum !== undefined && Array.isArray(value.enum)) {
        return value.enum.join(' or ');
    }
    return JSON.stringify(value);
}

/**
 * Formats array values for error messages
 * @param value The array value to format
 * @returns Formatted string value
 */
function formatArrayValue(value) {
    if (value.length === 2) {
        return value.join(' or ');
    }
    if (value.length > 2) {
        return value.slice(0, -1).join(', ') + ' or ' + value[value.length - 1];
    }
    return value[0];
}

/**
 * Formats any value for error messages
 * @param value The value to format
 * @returns Formatted string value
 */
function formatValue(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return formatObjectValue(value);
    }
    
    if (Array.isArray(value)) {
        return formatArrayValue(value);
    }
    
    if (value === null) {
        return 'null';
    }
    
    if (typeof value === 'number') {
        return value.toString();
    }
    
    return String(value);
}

/**
 * Validates basic error and schema requirements
 * @param error The error object
 * @param schema The validation schema
 * @returns True if valid, false otherwise
 */
function validateBasicRequirements(error, schema) {
    return !!(error && schema && error.schemaPath);
}

/**
 * Extracts condition from schema path
 * @param schema The validation schema
 * @param schemaPathParts The parsed schema path
 * @returns The condition object or null
 */
function extractCondition(schema, schemaPathParts) {
    const allOfIndex = schemaPathParts.indexOf('allOf');
    if (allOfIndex === -1) {
        return null;
    }

    const conditionIndex = parseInt(schemaPathParts[allOfIndex + 1]);
    if (isNaN(conditionIndex) || !schema.allOf?.[conditionIndex]) {
        return null;
    }

    return schema.allOf[conditionIndex];
}

/**
 * Returns the error message for a conditionally required field.
 * @param schema The validation schema
 * @param error The error objects
 */
export function getConditionallyRequiredErrorMessage(schema, error) {
    if (!validateBasicRequirements(error, schema)) {
        return 'This field is required';
    }

    const schemaPathParts = error.schemaPath.split('/');
    const condition = extractCondition(schema, schemaPathParts);
    
    if (!condition?.if) {
        return 'This field is required due to a conditional rule.';
    }

    const conditionalData = extractConditionalProperties(condition);
    if (!conditionalData) {
        return 'This field is required';
    }

    const { result, isElseCondition } = conditionalData;
    const [fieldName, value] = Object.entries(result)[0];
    const fieldLabel = getFieldLabel(schema, fieldName);
    const formattedValue = formatValue(value);

    if (schemaPathParts.includes('then')) {
        return replacePlaceholders(
            ERROR_MESSAGE_COPIES.mandatory.conditional, 
            fieldLabel, 
            isElseCondition ? "not " : "", 
            formattedValue
        );
    }
    
    if (schemaPathParts.includes('else')) {
        return replacePlaceholders(
            ERROR_MESSAGE_COPIES.mandatory.conditional, 
            fieldLabel, 
            isElseCondition ? "" : "not ", 
            formattedValue
        );
    }

    return 'This field is required due to a conditional rule.';
}
